import { Warning as WarningIcon } from '@mui/icons-material';
import { Card, CardContent, Grid } from '@mui/material';
import { SnackbarContentProps } from '@mui/material/SnackbarContent';
import { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { FC, useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import WideSnackbar from '@/components/bars/wide-snackbar';
import * as Highcharts from '@/components/containers/highcharts';
import { useTranslation } from '@/hooks';
import OEEWaterfallConfig from '@/lib/highcharts/oee-waterfall-config';

import { Data } from './oee-table-card';

const useStyles = makeStyles()((theme: Theme) => ({
  actions: {
    paddingLeft: theme.spacing(),
    paddingBottom: theme.spacing(),
  },
  contentHeader: {
    '& > div': {
      maxWidth: '100%',
    },
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    '@media print': {
      marginTop: '-2em',
    },
  },
  icon: {
    float: 'left',
    fontSize: 20,
    opacity: 0.9,
    marginRight: theme.spacing(),
    '@media print': {
      display: 'none',
    },
  },
}));

interface Properties {
  data: Data | null;
  loading: boolean;
  refetch: (start: Date, end: Date | null) => void;
  dataHash: string;
}

const OEEWaterfallCard: FC<Properties> = ({ data, refetch, loading, dataHash }) => {
  const [displayDeviations, setDisplayDeviations] = useState(false);
  const { classes } = useStyles();
  const theme = useTheme();
  const [t] = useTranslation(['shared', 'oee']);
  const router = useRouter();

  const toggleDisplayDeviations = useCallback(() => {
    setDisplayDeviations(!displayDeviations);
  }, [displayDeviations]);

  let positiveSpeedLossWarning: React.ReactElement<SnackbarContentProps> | undefined;
  // NOTE: All durations are positive numbers, but are reverted in the waterfall when displayed. This is why we
  // need to check for the speedloss being < 0, to indicate it being wrong.
  if (data && data.oee && data.oee.speedLoss < 0) {
    positiveSpeedLossWarning = (
      <WideSnackbar
        message={
          <>
            <WarningIcon className={classes.icon} />
            <span className={classes.message}>
              {t(['oee:speedlossWarning'], {
                defaultValue:
                  'Positive speedloss is a result of setting the validated line speed lower than the actual max line speed. We suggest to change the validated line speed to the max line speed',
              })}
            </span>
          </>
        }
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Grid item xs={12} sm={12} md={12}>
      <Card elevation={1}>
        <CardContent>
          {positiveSpeedLossWarning || null}
          <Highcharts.Graph
            compareForUpdate={[dataHash, displayDeviations].join('-')}
            aggressivelyMinimizeRendering
            configOptions={{
              height: '350px',
              refetch,
              timeRange: data.timeRange,
              stopStats: data.stopStats,
              oee: data.oee,
              theme,
              t,
              router,
              displayDeviations,
              toggleDisplayDeviations,
            }}
            configFn={OEEWaterfallConfig}
            loading={loading}
          />
        </CardContent>
      </Card>
    </Grid>
  );
};

export default OEEWaterfallCard;
